import PropTypes from 'prop-types'
import { useState } from 'react'

// Variation 1: Bold Numbers with Larger Text
const StageNavigationV1 = ({ currentStage = 1, onStageClick }) => {
  const [hoveredStage, setHoveredStage] = useState(null)

  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Dev' }
  ]

  const getStageStatus = (stageId) => {
    if (stageId < currentStage) return 'completed'
    if (stageId === currentStage) return 'current'
    return 'locked'
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    if (status === 'completed') {
      return {
        container: 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/50 hover:border-green-500/70 cursor-pointer',
        number: 'bg-green-500 text-white',
        text: 'text-green-300',
        subtext: 'text-green-400/70'
      }
    } else if (status === 'current') {
      return {
        container: 'bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border-blue-400 shadow-xl shadow-blue-500/30',
        number: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white',
        text: 'text-white',
        subtext: 'text-blue-200'
      }
    } else {
      return {
        container: 'bg-gray-800/60 border-gray-600/60 opacity-60',
        number: 'bg-gray-600 text-gray-300',
        text: 'text-gray-300',
        subtext: 'text-gray-500'
      }
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      {stages.map((stage, index) => {
        const styles = getStageStyles(stage.id)
        const status = getStageStatus(stage.id)
        const isHovered = hoveredStage === stage.id

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <div 
              className={`relative flex-1 rounded-xl border-2 p-4 transition-all duration-300 ${styles.container}`}
              onMouseEnter={() => setHoveredStage(stage.id)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => onStageClick?.(stage.id)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-xl ${styles.number} flex items-center justify-center flex-shrink-0 text-3xl font-bold shadow-lg`}>
                  {stage.id}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-lg font-bold ${styles.text} mb-0.5`}>
                    {stage.shortName}
                  </h3>
                  <p className={`text-xs ${styles.subtext}`}>
                    Stage {stage.id}
                  </p>
                </div>
              </div>

              {isHovered && status !== 'locked' && (
                <div className="absolute left-0 right-0 top-full mt-2 z-50 animate-fadeIn">
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl">
                    <h4 className="text-sm font-semibold text-white mb-1">{stage.name}</h4>
                  </div>
                </div>
              )}
            </div>

            {index < stages.length - 1 && (
              <div className="px-2 flex-shrink-0">
                <svg className={`w-4 h-4 ${status === 'completed' ? 'text-green-500' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Variation 2: Vertical Number Badges
const StageNavigationV2 = ({ currentStage = 1, onStageClick }) => {
  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Dev' }
  ]

  const getStageStatus = (stageId) => {
    if (stageId < currentStage) return 'completed'
    if (stageId === currentStage) return 'current'
    return 'locked'
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    if (status === 'completed') {
      return {
        container: 'bg-gradient-to-br from-green-500/15 to-green-600/15 border-green-500/40',
        badge: 'bg-green-500',
        text: 'text-green-200',
        title: 'text-white'
      }
    } else if (status === 'current') {
      return {
        container: 'bg-gradient-to-br from-blue-500/25 to-cyan-500/25 border-blue-400 shadow-lg shadow-blue-500/20',
        badge: 'bg-gradient-to-br from-blue-500 to-cyan-500',
        text: 'text-blue-200',
        title: 'text-white'
      }
    } else {
      return {
        container: 'bg-gray-800/50 border-gray-600/50',
        badge: 'bg-gray-600',
        text: 'text-gray-400',
        title: 'text-gray-200'
      }
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      {stages.map((stage, index) => {
        const styles = getStageStyles(stage.id)
        const status = getStageStatus(stage.id)

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <div 
              className={`relative flex-1 rounded-xl border-2 p-4 transition-all duration-300 ${styles.container} cursor-pointer hover:scale-105`}
              onClick={() => onStageClick?.(stage.id)}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <div className={`w-12 h-12 rounded-full ${styles.badge} flex items-center justify-center text-2xl font-bold text-white shadow-lg`}>
                  {stage.id}
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${styles.title}`}>
                    {stage.shortName}
                  </h3>
                  <p className={`text-xs ${styles.text} mt-0.5`}>
                    Stage {stage.id}
                  </p>
                </div>
              </div>
            </div>

            {index < stages.length - 1 && (
              <div className="px-2 flex-shrink-0">
                <div className={`w-8 h-0.5 ${status === 'completed' ? 'bg-green-500' : 'bg-gray-600'}`}></div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Variation 3: Large Numbers Overlay
const StageNavigationV3 = ({ currentStage = 1, onStageClick }) => {
  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Dev' }
  ]

  const getStageStatus = (stageId) => {
    if (stageId < currentStage) return 'completed'
    if (stageId === currentStage) return 'current'
    return 'locked'
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    if (status === 'completed') {
      return {
        container: 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/50',
        numberBg: 'text-green-500/20',
        number: 'text-green-400',
        text: 'text-green-200',
        title: 'text-white'
      }
    } else if (status === 'current') {
      return {
        container: 'bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border-blue-400 shadow-xl',
        numberBg: 'text-blue-500/20',
        number: 'text-cyan-400',
        text: 'text-blue-200',
        title: 'text-white'
      }
    } else {
      return {
        container: 'bg-gray-800/70 border-gray-600',
        numberBg: 'text-gray-700/40',
        number: 'text-gray-400',
        text: 'text-gray-400',
        title: 'text-gray-200'
      }
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      {stages.map((stage, index) => {
        const styles = getStageStyles(stage.id)
        const status = getStageStatus(stage.id)

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <div 
              className={`relative flex-1 rounded-xl border-2 p-5 transition-all duration-300 ${styles.container} cursor-pointer overflow-hidden`}
              onClick={() => onStageClick?.(stage.id)}
            >
              <div className={`absolute top-0 right-0 text-9xl font-black ${styles.numberBg} leading-none -mr-4 -mt-2`}>
                {stage.id}
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-4xl font-black ${styles.number}`}>{stage.id}</span>
                  <div className={`h-px flex-1 ${status === 'current' ? 'bg-gradient-to-r from-blue-400 to-transparent' : 'bg-gray-600'}`}></div>
                </div>
                <h3 className={`text-base font-bold ${styles.title} mb-0.5`}>
                  {stage.shortName}
                </h3>
                <p className={`text-xs ${styles.text}`}>
                  Stage {stage.id}
                </p>
              </div>
            </div>

            {index < stages.length - 1 && (
              <div className="px-2 flex-shrink-0">
                <svg className={`w-5 h-5 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Variation 4: Side Number with Progress
const StageNavigationV4 = ({ currentStage = 1, onStageClick }) => {
  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Dev' }
  ]

  const getStageStatus = (stageId) => {
    if (stageId < currentStage) return 'completed'
    if (stageId === currentStage) return 'current'
    return 'locked'
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    if (status === 'completed') {
      return {
        container: 'bg-gradient-to-r from-green-500/25 via-green-600/15 to-green-700/10 border-green-500/60',
        number: 'bg-green-500 text-white border-green-400',
        text: 'text-green-200',
        title: 'text-white',
        progress: 'bg-green-500'
      }
    } else if (status === 'current') {
      return {
        container: 'bg-gradient-to-r from-blue-500/35 via-cyan-500/25 to-blue-600/15 border-blue-400 shadow-lg',
        number: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white border-cyan-400',
        text: 'text-blue-100',
        title: 'text-white',
        progress: 'bg-gradient-to-r from-blue-500 to-cyan-500'
      }
    } else {
      return {
        container: 'bg-gray-800/60 border-gray-600/70',
        number: 'bg-gray-700 text-gray-300 border-gray-600',
        text: 'text-gray-400',
        title: 'text-gray-200',
        progress: 'bg-gray-700'
      }
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      {stages.map((stage, index) => {
        const styles = getStageStyles(stage.id)
        const status = getStageStatus(stage.id)

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <div 
              className={`relative flex-1 rounded-xl border-2 p-4 transition-all duration-300 ${styles.container} cursor-pointer`}
              onClick={() => onStageClick?.(stage.id)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-16 h-16 rounded-lg ${styles.number} border-2 flex flex-col items-center justify-center flex-shrink-0 shadow-lg`}>
                  <span className="text-3xl font-black leading-none">{stage.id}</span>
                  <span className="text-[8px] uppercase font-bold opacity-70 mt-0.5">Stage</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-base font-bold ${styles.title} mb-1`}>
                    {stage.shortName}
                  </h3>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div className={`${styles.progress} h-1.5 rounded-full transition-all`} style={{ width: status === 'completed' ? '100%' : status === 'current' ? '50%' : '0%' }}></div>
                  </div>
                  <p className={`text-xs ${styles.text} mt-1`}>
                    {status === 'completed' ? 'Complete' : status === 'current' ? 'In Progress' : 'Not Started'}
                  </p>
                </div>
              </div>
            </div>

            {index < stages.length - 1 && (
              <div className="px-2 flex-shrink-0">
                <svg className={`w-4 h-4 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Variation 5: Minimal with Bright Future Stages
const StageNavigationV5 = ({ currentStage = 1, onStageClick }) => {
  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Dev' }
  ]

  const getStageStatus = (stageId) => {
    if (stageId < currentStage) return 'completed'
    if (stageId === currentStage) return 'current'
    return 'locked'
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    if (status === 'completed') {
      return {
        container: 'bg-green-500/15 border-green-500/40 hover:bg-green-500/25',
        number: 'bg-green-500 text-white',
        text: 'text-green-200',
        title: 'text-white font-bold'
      }
    } else if (status === 'current') {
      return {
        container: 'bg-blue-500/25 border-blue-400 shadow-xl shadow-blue-500/25',
        number: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white ring-4 ring-blue-400/30',
        text: 'text-blue-100',
        title: 'text-white font-bold'
      }
    } else {
      return {
        container: 'bg-gray-700/40 border-gray-500/50 hover:bg-gray-700/60',
        number: 'bg-gray-500 text-white',
        text: 'text-gray-300',
        title: 'text-gray-100 font-semibold'
      }
    }
  }

  return (
    <div className="flex items-center justify-between gap-4">
      {stages.map((stage, index) => {
        const styles = getStageStyles(stage.id)
        const status = getStageStatus(stage.id)

        return (
          <div key={stage.id} className="flex items-center flex-1">
            <div 
              className={`relative flex-1 rounded-2xl border-2 p-5 transition-all duration-300 ${styles.container} cursor-pointer`}
              onClick={() => onStageClick?.(stage.id)}
            >
              <div className="flex flex-col items-center text-center gap-3">
                <div className={`w-16 h-16 rounded-2xl ${styles.number} flex items-center justify-center text-4xl font-black shadow-xl transition-all`}>
                  {stage.id}
                </div>
                <div>
                  <h3 className={`text-sm ${styles.title} mb-1 leading-tight`}>
                    {stage.shortName}
                  </h3>
                  <p className={`text-xs ${styles.text} font-medium`}>
                    Stage {stage.id}
                  </p>
                </div>
              </div>
            </div>

            {index < stages.length - 1 && (
              <div className="flex-shrink-0">
                <div className={`w-6 h-0.5 ${status === 'completed' ? 'bg-green-500' : 'bg-gray-600'} transition-colors`}></div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

StageNavigationV1.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func
}

StageNavigationV2.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func
}

StageNavigationV3.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func
}

StageNavigationV4.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func
}

StageNavigationV5.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func
}

// Variation 6: V3 Overlay + V4 Progress Bar
const StageNavigationV6 = ({ currentStage = 1, onStageClick, currentIdea = null }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  
  const stages = [
    { id: 1, name: 'Opportunity Assessment', shortName: 'Opportunity Assessment' },
    { id: 2, name: 'Commercial Ideation', shortName: 'Commercial Ideation' },
    { id: 3, name: 'Business Case', shortName: 'Business Case' },
    { id: 4, name: 'UX Design', shortName: 'UX Design' },
    { id: 5, name: 'MVP Development', shortName: 'MVP Development' }
  ]

  const isStageComplete = (stageId) => {
    // Stage 1 is complete when there's an opportunity assessment score
    if (stageId === 1) {
      return currentIdea?.opportunity_assessment?.score !== undefined && currentIdea?.opportunity_assessment?.score !== null
    }
    // Other stages are not completable yet
    return false
  }

  const getStageStatus = (stageId) => {
    if (isStageComplete(stageId)) return 'completed'
    // Only Stage 1 can be "current" (in progress) since other stages aren't built yet
    if (stageId === 1 && stageId === currentStage && !isStageComplete(stageId)) return 'current'
    return 'locked'
  }

  const getStageProgress = (stageId) => {
    // For Stage 1 (Opportunity Assessment), calculate progress based on 5 steps
    if (stageId === 1) {
      let completed = 0
      // Count triage as completed if assessment exists OR if force-progressed
      if (currentIdea?.triage_assessment || 
          currentIdea?.status === 'opportunity-assessment' ||
          currentIdea?.status === 'search-terms-generated' ||
          currentIdea?.status === 'market-demand-evaluated' ||
          currentIdea?.status === 'market-saturation-evaluated' ||
          currentIdea?.status === 'usp-evaluated' ||
          currentIdea?.status === 'opportunity-assessed') completed++
      if (currentIdea?.market_demand_evaluation) completed++
      if (currentIdea?.market_saturation_evaluation) completed++
      if (currentIdea?.usp_evaluation) completed++
      if (currentIdea?.opportunity_assessment) completed++
      return (completed / 5) * 100
    }
    // Other stages return 0 progress
    return 0
  }

  const getStageStyles = (stageId) => {
    const status = getStageStatus(stageId)
    
    // Define stage-specific colors for the number
    const stageColors = {
      1: { number: 'text-cyan-400' },
      2: { number: 'text-teal-400' },
      3: { number: 'text-orange-400' },
      4: { number: 'text-fuchsia-400' },
      5: { number: 'text-indigo-400' }
    }
    
    const colors = stageColors[stageId]
    
    // Progress bar color indicates status
    let progressColor = 'bg-gray-700' // Not started
    if (status === 'current') {
      progressColor = 'bg-gradient-to-r from-blue-500 to-blue-600'
    } else if (status === 'completed') {
      progressColor = 'bg-green-500'
    }
    
    // All boxes use the same neutral style with white gradient, only progress bar changes
    return {
      container: 'bg-gray-800/70 border-gray-600',
      numberBg: 'text-gray-700/40',
      number: colors.number,
      text: 'text-gray-400',
      title: 'text-gray-200',
      progress: progressColor,
      bgGradient: 'from-white/15'
    }
  }

  return (
    <>
      {/* Mobile: Expandable accordion */}
      <div className="md:hidden w-full">
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-full rounded-2xl shadow-none p-4 transition-all duration-300 bg-gray-800/70 hover:bg-gray-800/80 cursor-pointer outline -outline-offset-1 outline-white/10 relative overflow-hidden"
        >
          {/* Top border glow */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none"></div>
          
          {/* Background gradient decorations */}
          <div className="absolute top-0 left-0 w-40 h-20 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-xl pointer-events-none"></div>
          
          <div className="relative z-10 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="text-base font-display tracking-tight text-gray-200 font-medium">
                  Stages
                </h3>
                <p className="text-xs text-gray-400">
                  {isDropdownOpen ? 'Tap to collapse' : `Stage ${currentStage} of ${stages.length}`}
                </p>
              </div>
            </div>
            <svg className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {/* Expanded stages list */}
        {isDropdownOpen && (
          <div className="mt-3 space-y-2">
            {stages.map((stage, index) => {
              const styles = getStageStyles(stage.id)
              const status = getStageStatus(stage.id)
              const isSelected = stage.id === currentStage
              
              return (
                <div key={stage.id}>
                  <button
                    onClick={() => onStageClick?.(stage.id)}
                    className={`w-full rounded-xl p-4 transition-all duration-300 ${styles.container} cursor-pointer outline -outline-offset-1 outline-white/10 relative overflow-hidden ${
                      isSelected ? 'ring-2 ring-white/20' : ''
                    }`}
                  >
                    {/* Top border glow */}
                    <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
                      isSelected ? 'via-white/60' : 'via-white/40'
                    } to-transparent pointer-events-none`}></div>
                    
                    {/* Background gradient decorations */}
                    <div className={`absolute top-0 left-0 w-32 h-16 bg-gradient-to-br ${styles.bgGradient} via-transparent to-transparent rounded-tl-xl pointer-events-none`}></div>
                    
                    {/* Large background number */}
                    <div className={`absolute top-0 right-0 text-6xl font-black ${styles.numberBg} leading-none -mr-2 -mt-1 pointer-events-none`}>
                      {stage.id}
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-2xl font-black ${styles.number}`}>{stage.id}</span>
                        <h3 className={`text-sm font-display tracking-tight ${styles.title} leading-tight flex-1`}>
                          {stage.shortName}
                        </h3>
                        {isSelected && (
                          <svg className="w-5 h-5 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1.5">
                        <div 
                          className={`${styles.progress} h-1.5 rounded-full transition-all duration-700`} 
                          style={{ width: status === 'completed' ? '100%' : status === 'current' ? `${getStageProgress(stage.id)}%` : '0%' }}
                        ></div>
                      </div>
                      <p className={`text-xs ${styles.text} font-medium`}>
                        {status === 'completed' ? 'Complete' : status === 'current' ? 'In Progress' : 'Not Started'}
                      </p>
                    </div>
                  </button>
                  
                  {/* Arrow between stages */}
                  {index < stages.length - 1 && (
                    <div className="flex items-center justify-center h-6">
                      <svg className={`w-4 h-4 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Tablet: 2-row grid layout with arrows */}
      <div className="hidden md:block lg:hidden w-full">
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] gap-0 w-full">
          {/* Row 1: Stages 1, 2, 3 */}
          {stages.slice(0, 3).map((stage, index) => {
            const styles = getStageStyles(stage.id)
            const status = getStageStatus(stage.id)
            const isSelected = stage.id === currentStage
            const hasArrow = index < 2

            return (
              <>
                <div key={`tablet-${stage.id}`} className="relative overflow-hidden">
                  <div 
                    className={`rounded-xl shadow-none p-4 transition-all duration-300 ${styles.container} cursor-pointer h-[110px] flex flex-col outline -outline-offset-1 outline-white/10 ${
                      isSelected ? 'ring-2 ring-white/20' : ''
                    }`}
                    onClick={() => onStageClick?.(stage.id)}
                  >
                    {/* Top border glow */}
                    <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
                      isSelected ? 'via-white/60' : 'via-white/40'
                    } to-transparent pointer-events-none`}></div>
                    
                    {/* Background gradient decorations */}
                    <div className={`absolute top-0 left-0 w-32 h-16 bg-gradient-to-br ${styles.bgGradient} via-transparent to-transparent rounded-tl-xl pointer-events-none`}></div>
                    
                    {/* Large background number */}
                    <div className={`absolute top-0 right-0 text-6xl font-black ${styles.numberBg} leading-none -mr-2 -mt-1 pointer-events-none`}>
                      {stage.id}
                    </div>
                    
                    <div className="relative z-10 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-2xl font-black ${styles.number}`}>{stage.id}</span>
                        <h3 className={`text-sm font-display tracking-tight ${styles.title} leading-tight flex-1`}>
                          {stage.shortName}
                        </h3>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-auto">
                        <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1.5">
                          <div 
                            className={`${styles.progress} h-1.5 rounded-full transition-all duration-700`} 
                            style={{ width: status === 'completed' ? '100%' : status === 'current' ? `${getStageProgress(stage.id)}%` : '0%' }}
                          ></div>
                        </div>
                        <p className={`text-xs ${styles.text} font-medium`}>
                          {status === 'completed' ? 'Complete' : status === 'current' ? 'In Progress' : 'Not Started'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right arrow between stages in row 1 */}
                {hasArrow && (
                  <div key={`arrow-${stage.id}`} className="flex items-center justify-center w-8">
                    <svg className={`w-4 h-4 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </>
            )
          })}
        </div>
        
        {/* Down arrow between rows */}
        <div className="flex items-center justify-center h-8 w-full">
          <svg className={`w-4 h-4 ${getStageStatus(3) === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        
        <div className="grid grid-cols-[1fr_auto_1fr_1fr] gap-0 w-full">
          {/* Row 2: Stages 4, 5 (centered with empty third column) */}
          {stages.slice(3, 5).map((stage, index) => {
            const styles = getStageStyles(stage.id)
            const status = getStageStatus(stage.id)
            const isSelected = stage.id === currentStage
            const hasArrow = index < 1

            return (
              <>
                <div key={`tablet-${stage.id}`} className="relative overflow-hidden">
                  <div 
                    className={`rounded-xl shadow-none p-4 transition-all duration-300 ${styles.container} cursor-pointer h-[110px] flex flex-col outline -outline-offset-1 outline-white/10 ${
                      isSelected ? 'ring-2 ring-white/20' : ''
                    }`}
                    onClick={() => onStageClick?.(stage.id)}
                  >
                    {/* Top border glow */}
                    <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
                      isSelected ? 'via-white/60' : 'via-white/40'
                    } to-transparent pointer-events-none`}></div>
                    
                    {/* Background gradient decorations */}
                    <div className={`absolute top-0 left-0 w-32 h-16 bg-gradient-to-br ${styles.bgGradient} via-transparent to-transparent rounded-tl-xl pointer-events-none`}></div>
                    
                    {/* Large background number */}
                    <div className={`absolute top-0 right-0 text-6xl font-black ${styles.numberBg} leading-none -mr-2 -mt-1 pointer-events-none`}>
                      {stage.id}
                    </div>
                    
                    <div className="relative z-10 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-2xl font-black ${styles.number}`}>{stage.id}</span>
                        <h3 className={`text-sm font-display tracking-tight ${styles.title} leading-tight flex-1`}>
                          {stage.shortName}
                        </h3>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-auto">
                        <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1.5">
                          <div 
                            className={`${styles.progress} h-1.5 rounded-full transition-all duration-700`} 
                            style={{ width: status === 'completed' ? '100%' : status === 'current' ? `${getStageProgress(stage.id)}%` : '0%' }}
                          ></div>
                        </div>
                        <p className={`text-xs ${styles.text} font-medium`}>
                          {status === 'completed' ? 'Complete' : status === 'current' ? 'In Progress' : 'Not Started'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right arrow between stages 4 and 5 */}
                {hasArrow && (
                  <div key={`arrow-${stage.id}`} className="flex items-center justify-center w-8">
                    <svg className={`w-4 h-4 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </>
            )
          })}
          {/* Empty third column to center stages 4 and 5 */}
          <div></div>
        </div>
      </div>

      {/* Desktop: Horizontal layout */}
      <div className="hidden lg:flex lg:items-center gap-0 w-full">
        {stages.map((stage, index) => {
          const styles = getStageStyles(stage.id)
          const status = getStageStatus(stage.id)
          const hasArrow = index < stages.length - 1
          const isSelected = stage.id === currentStage

          return (
            <>
              <div 
                key={`box-${stage.id}`}
                className="relative overflow-hidden flex-1"
              >
                <div 
                  className={`rounded-2xl shadow-none p-5 transition-all duration-300 ${styles.container} cursor-pointer h-[120px] flex flex-col outline -outline-offset-1 outline-white/10`}
                  onClick={() => onStageClick?.(stage.id)}
                >
                  {/* Top border glow */}
                  <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${
                    isSelected ? 'via-white/60' : 'via-white/40'
                  } to-transparent pointer-events-none`}></div>
                  
                  {/* Bottom border glow - only show when selected */}
                  {isSelected && (
                    <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none`}></div>
                  )}
                  
                  {/* Background gradient decorations */}
                  <div className={`absolute top-0 left-0 w-40 h-20 bg-gradient-to-br ${styles.bgGradient} via-transparent to-transparent rounded-tl-xl pointer-events-none`}></div>
                  
                  {/* Large background number */}
                  <div className={`absolute top-0 right-0 text-9xl font-black ${styles.numberBg} leading-none -mr-4 -mt-2 pointer-events-none`}>
                    {stage.id}
                  </div>
                  
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-4xl font-black ${styles.number}`}>{stage.id}</span>
                      <h3 className={`text-base font-display tracking-tight ${styles.title} leading-tight`}>
                        {stage.shortName}
                      </h3>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-auto pt-2">
                      <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1.5">
                        <div 
                          className={`${styles.progress} h-1.5 rounded-full transition-all duration-700`} 
                          style={{ width: status === 'completed' ? '100%' : status === 'current' ? `${getStageProgress(stage.id)}%` : '0%' }}
                        ></div>
                      </div>
                      <p className={`text-xs ${styles.text} font-medium`}>
                        {status === 'completed' ? 'Complete' : status === 'current' ? 'In Progress' : 'Not Started'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {hasArrow && (
                <div key={`arrow-${stage.id}`} className="flex items-center justify-center h-[120px] w-12 flex-shrink-0">
                  <svg className={`w-5 h-5 ${status === 'completed' ? 'text-green-500' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </>
          )
        })}
      </div>
    </>
  )
}

StageNavigationV6.propTypes = {
  currentStage: PropTypes.number,
  onStageClick: PropTypes.func,
  currentIdea: PropTypes.object
}

export { StageNavigationV1, StageNavigationV2, StageNavigationV3, StageNavigationV4, StageNavigationV5, StageNavigationV6 }
